import { useState, useMemo, useEffect, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Search, Car, ChevronDown, ChevronRight, Plus, Pencil, X, Trash2, Loader2 } from 'lucide-react';
import { makeModelData as fallbackData } from '../data/makeModelData';
import ExcelUpload from '../components/ExcelUpload';
import apiService from '../services/api';

const MakeModelList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMake, setSelectedMake] = useState('all');
  const [expandedMakes, setExpandedMakes] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [isAddMakeOpen, setIsAddMakeOpen] = useState(false);
  const [isEditMakeOpen, setIsEditMakeOpen] = useState(false);
  const [editingMake, setEditingMake] = useState(null);
  const [newMakeName, setNewMakeName] = useState('');
  const [newModels, setNewModels] = useState('');

  // Excel upload configuration
  const excelExpectedColumns = ['Make', 'Models'];
  const excelTemplateData = [
    { Make: 'Toyota', Models: 'Camry, Corolla, RAV4' },
    { Make: 'Honda', Models: 'Civic, Accord, CR-V' }
  ];

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Try grouped endpoint first (protected, has model IDs)
      const response = await apiService.getMasterVehicleBrandsGrouped();
      if (response.success && response.data?.brands) {
        // Transform API data to our format
        const transformedData = response.data.brands.map(brand => ({
          id: brand.masterVehiclesBrandId,
          make: brand.masterVehiclesBrandName || 'Unknown',
          models: brand.models.map(model => ({
            id: model.id,
            name: model.modelName || 'Unknown'
          }))
        }));
        setData(transformedData);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching from grouped endpoint, trying public endpoint:', err);
      try {
        // Fallback to public endpoint (no model IDs)
        const response = await apiService.getMasterVehicleBrands();
        if (response.success && response.data?.brands) {
          const transformedData = response.data.brands.map(brand => ({
            id: brand.id,
            make: brand.name || 'Unknown',
            models: brand.models.map((model, idx) => ({
              id: `temp-${idx}`,
              name: model.modelName || 'Unknown'
            }))
          }));
          setData(transformedData);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (publicErr) {
        console.error('Error fetching vehicle brands:', publicErr);
        setError('Failed to load data from server. Showing offline data.');
        // Use fallback data
        setData(fallbackData.map((item, idx) => ({
          id: `fallback-${idx}`,
          make: item.make,
          models: item.models.map((model, midx) => ({
            id: `fallback-${idx}-${midx}`,
            name: model
          }))
        })));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Excel data import
  const handleExcelImport = (excelData) => {
    const newEntries = excelData.map(row => ({
      id: `new-${Date.now()}-${Math.random()}`,
      make: row.Make || row.make || '',
      models: (row.Models || row.models || '')
        .split(',')
        .map((m, idx) => ({ id: `new-model-${Date.now()}-${idx}`, name: m.trim() }))
        .filter(m => m.name.length > 0)
    })).filter(entry => entry.make);

    // Merge with existing data
    setData(prev => {
      const updated = [...prev];
      newEntries.forEach(newEntry => {
        const existingIndex = updated.findIndex(
          item => item.make.toLowerCase() === newEntry.make.toLowerCase()
        );
        if (existingIndex >= 0) {
          const existingModelNames = new Set(updated[existingIndex].models.map(m => m.name));
          newEntry.models.forEach(m => {
            if (!existingModelNames.has(m.name)) {
              updated[existingIndex].models.push(m);
            }
          });
        } else {
          updated.push(newEntry);
        }
      });
      return updated.sort((a, b) => a.make.localeCompare(b.make));
    });
  };

  // Get unique makes for filter dropdown
  const makes = useMemo(() => {
    return data.map(item => item.make).sort();
  }, [data]);

  // Filter data based on search and make filter
  const filteredData = useMemo(() => {
    return data
      .filter(item => {
        if (selectedMake !== 'all' && item.make !== selectedMake) {
          return false;
        }
        
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const makeMatch = item.make.toLowerCase().includes(query);
          const modelMatch = item.models.some(model => 
            model.name.toLowerCase().includes(query)
          );
          return makeMatch || modelMatch;
        }
        
        return true;
      })
      .map(item => {
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const filteredModels = item.models.filter(model =>
            model.name.toLowerCase().includes(query) || 
            item.make.toLowerCase().includes(query)
          );
          return { ...item, models: filteredModels };
        }
        return item;
      });
  }, [searchQuery, selectedMake, data]);

  // Count total models
  const totalModels = useMemo(() => {
    return filteredData.reduce((acc, item) => acc + item.models.length, 0);
  }, [filteredData]);

  const toggleExpand = (make) => {
    setExpandedMakes(prev => ({
      ...prev,
      [make]: !prev[make]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    filteredData.forEach(item => {
      allExpanded[item.make] = true;
    });
    setExpandedMakes(allExpanded);
  };

  const collapseAll = () => {
    setExpandedMakes({});
  };

  // Add new make (local only - API doesn't support create)
  const handleAddMake = () => {
    if (!newMakeName.trim()) return;
    
    const modelsArray = newModels
      .split(',')
      .map((m, idx) => ({ id: `new-model-${Date.now()}-${idx}`, name: m.trim() }))
      .filter(m => m.name.length > 0);
    
    const newEntry = {
      id: `new-${Date.now()}`,
      make: newMakeName.trim(),
      models: modelsArray
    };
    
    setData(prev => [...prev, newEntry].sort((a, b) => a.make.localeCompare(b.make)));
    setNewMakeName('');
    setNewModels('');
    setIsAddMakeOpen(false);
  };

  // Open edit dialog
  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setEditingMake(item);
    setNewMakeName(item.make);
    setNewModels(item.models.map(m => m.name).join(', '));
    setIsEditMakeOpen(true);
  };

  // Save edit - calls API to update brand name and models
  const handleSaveEdit = async () => {
    if (!newMakeName.trim() || !editingMake) return;
    
    setSaving(true);
    try {
      const modelsToUpdate = newModels
        .split(',')
        .map(m => m.trim())
        .filter(m => m.length > 0);
      
      // Build the PATCH request body
      const updateData = {
        brandName: newMakeName.trim()
      };
      
      // If we have model IDs, we can update model names too
      if (editingMake.models.length > 0 && !editingMake.id.startsWith('fallback') && !editingMake.id.startsWith('new')) {
        const modelsWithIds = editingMake.models
          .map((model, idx) => ({
            id: model.id,
            modelName: modelsToUpdate[idx] || model.name
          }))
          .filter(m => m.id && !m.id.startsWith('temp') && !m.id.startsWith('new') && !m.id.startsWith('fallback'));
        
        if (modelsWithIds.length > 0) {
          updateData.models = modelsWithIds;
        }
      }
      
      // Only call API if we have a real brand ID
      if (editingMake.id && !editingMake.id.startsWith('fallback') && !editingMake.id.startsWith('new')) {
        await apiService.updateMasterVehicleBrand(editingMake.id, updateData);
      }
      
      // Update local state
      setData(prev => prev.map(item => 
        item.id === editingMake.id 
          ? { 
              ...item, 
              make: newMakeName.trim(), 
              models: modelsToUpdate.map((name, idx) => ({
                id: editingMake.models[idx]?.id || `updated-${Date.now()}-${idx}`,
                name
              }))
            }
          : item
      ).sort((a, b) => a.make.localeCompare(b.make)));
      
      setEditingMake(null);
      setNewMakeName('');
      setNewModels('');
      setIsEditMakeOpen(false);
    } catch (err) {
      console.error('Error updating brand:', err);
      alert('Failed to save changes. ' + (err.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Delete make (local only - API doesn't support delete)
  const handleDeleteMake = (e, makeId, makeName) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${makeName}" and all its models?`)) {
      setData(prev => prev.filter(item => item.id !== makeId));
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
          <p className="text-[#94A3B8]">Loading vehicle brands...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="make-model-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Make & Model Master List
          </h1>
          <p className="text-[#94A3B8]">
            Browse all supported vehicle makes and models
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExcelUpload
            onDataParsed={handleExcelImport}
            expectedColumns={excelExpectedColumns}
            templateData={excelTemplateData}
            templateFileName="make_model_template.xlsx"
            title="Upload Make-Model Data"
          />
          <Button 
            onClick={() => setIsAddMakeOpen(true)}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="add-make-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Make
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg text-[#F59E0B]">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
              <Car className="w-5 h-5 text-[#2563EB]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#E2E8F0]">{filteredData.length}</p>
              <p className="text-sm text-[#64748B]">Makes</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
              <Car className="w-5 h-5 text-[#22C55E]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#E2E8F0]">{totalModels}</p>
              <p className="text-sm text-[#64748B]">Models</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
          <Input
            type="text"
            placeholder="Search makes or models..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#1E293B] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11"
            data-testid="search-input"
          />
        </div>
        <Select value={selectedMake} onValueChange={setSelectedMake}>
          <SelectTrigger className="w-full sm:w-[200px] bg-[#1E293B] border-[#334155] text-[#E2E8F0]" data-testid="make-filter">
            <SelectValue placeholder="Filter by Make" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-[#334155]">
            <SelectItem value="all" className="text-[#E2E8F0] focus:bg-[#334155]">All Makes</SelectItem>
            {makes.map(make => (
              <SelectItem key={make} value={make} className="text-[#E2E8F0] focus:bg-[#334155]">
                {make}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-2 text-sm text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#334155] rounded-md transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-2 text-sm text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#334155] rounded-md transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#334155] hover:bg-transparent">
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider w-12"></TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Make</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider text-center">Models Count</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Models</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-[#64748B]">
                    No makes or models found matching your search.
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item) => (
                  <TableRow
                    key={item.id}
                    className="border-b border-[#334155]/50 hover:bg-[#334155]/30 cursor-pointer transition-colors"
                    onClick={() => toggleExpand(item.make)}
                    data-testid={`make-row-${item.make}`}
                  >
                    <TableCell className="w-12">
                      {expandedMakes[item.make] ? (
                        <ChevronDown className="w-5 h-5 text-[#64748B]" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-[#64748B]" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-[#E2E8F0]">
                      {item.make}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-[#2563EB]/20 text-[#60A5FA] border-[#2563EB]/30">
                        {item.models.length}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#94A3B8]">
                      {expandedMakes[item.make] ? (
                        <div className="flex flex-wrap gap-2 py-2">
                          {item.models.map((model) => (
                            <Badge 
                              key={model.id} 
                              variant="outline" 
                              className="bg-[#334155] text-[#E2E8F0] border-[#475569]"
                            >
                              {model.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm">
                          {item.models.slice(0, 3).map(m => m.name).join(', ')}
                          {item.models.length > 3 && ` +${item.models.length - 3} more`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleEditClick(e, item)}
                          className="h-8 w-8 text-[#64748B] hover:text-[#E2E8F0] hover:bg-[#334155]"
                          data-testid={`edit-make-${item.make}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleDeleteMake(e, item.id, item.make)}
                          className="h-8 w-8 text-[#64748B] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                          data-testid={`delete-make-${item.make}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add Make Dialog */}
      <Dialog open={isAddMakeOpen} onOpenChange={setIsAddMakeOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E2E8F0] flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#2563EB]" />
              Add New Make
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#94A3B8]">Make Name</Label>
              <Input
                value={newMakeName}
                onChange={(e) => setNewMakeName(e.target.value)}
                placeholder="e.g., Toyota"
                className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
                data-testid="make-name-input"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Models (comma separated)</Label>
              <Input
                value={newModels}
                onChange={(e) => setNewModels(e.target.value)}
                placeholder="e.g., Camry, Corolla, RAV4"
                className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
                data-testid="models-input"
              />
              <p className="text-xs text-[#64748B] mt-1">Enter model names separated by commas</p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsAddMakeOpen(false)}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMake}
              disabled={!newMakeName.trim()}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="save-make-button"
            >
              Add Make
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Make Dialog */}
      <Dialog open={isEditMakeOpen} onOpenChange={setIsEditMakeOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E2E8F0] flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#F59E0B]" />
              Edit Make
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#94A3B8]">Make Name</Label>
              <Input
                value={newMakeName}
                onChange={(e) => setNewMakeName(e.target.value)}
                placeholder="e.g., Toyota"
                className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
                data-testid="edit-make-name-input"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Models (comma separated)</Label>
              <Input
                value={newModels}
                onChange={(e) => setNewModels(e.target.value)}
                placeholder="e.g., Camry, Corolla, RAV4"
                className="mt-1 bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B]"
                data-testid="edit-models-input"
              />
              <p className="text-xs text-[#64748B] mt-1">Enter model names separated by commas</p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditMakeOpen(false)}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={!newMakeName.trim() || saving}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
              data-testid="update-make-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Update Make'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MakeModelList;
